import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useAuth, useClerk } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { IconType } from 'react-icons';
import {
  FiActivity,
  FiAlignLeft,
  FiBarChart2,
  FiBook,
  FiChevronDown,
  FiClipboard,
  FiClock,
  FiDollarSign,
  FiFolder,
  FiGlobe,
  FiImage,
  FiLink,
  FiPieChart,
  FiTrendingUp,
  FiFileText,
  FiFilter,
  FiFlag,
  FiLogOut,
  FiMail,
  FiMenu,
  FiMessageCircle,
  FiSearch,
  FiSend,
  FiSmartphone,
  FiTarget,
  FiTool,
  FiUsers,
  FiVideo,
  FiX,
} from 'react-icons/fi';
import { AdminUnreadDot } from '@/components/admin/AdminUnreadDot';
import { apiJson } from '@/lib/api';
import { COACH_OUTREACH_REPLIES_QUERY_KEY } from '@/lib/coachOutreachRepliesStorage';
import { useCoachOutreachUnreadReplies } from '@/hooks/useCoachOutreachUnreadReplies';
import { LAWYER_OUTREACH_REPLIES_QUERY_KEY } from '@/lib/lawyerOutreachRepliesStorage';
import { useLawyerOutreachUnreadReplies } from '@/hooks/useLawyerOutreachUnreadReplies';
import type {
  ProfessionalDirectoryProfileListResponse,
  UserResponse,
} from '@/lib/types';
import faviconPng from '@/assets/favicon.png';

type OutreachRepliesListResponse = {
  replies: Array<{ received_at: string | null }>;
};

const ADMIN_DASHBOARD_HREF = import.meta.env.DEV
  ? 'http://localhost:5173/admin/'
  : 'https://harborcoparent.com/admin/';

type AdminNavItem = {
  to: string;
  label: string;
  icon: IconType;
  active: boolean;
  search?: Record<string, unknown>;
  showPendingBadge?: boolean;
  showUnreadBadge?: boolean;
  unreadBadgeLabel?: string;
};

type AdminNavGroup = {
  id: string;
  label: string;
  items: AdminNavItem[];
};

function AdminLayout() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);
  const desktopNavRef = useRef<HTMLElement>(null);

  const userQuery = useQuery({
    queryKey: ['admin', 'user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
    enabled: isLoaded && !!isSignedIn,
    retry: 1,
  });

  const pendingDirectoryProfilesQuery = useQuery({
    queryKey: ['admin-directory-profiles', 'pending'],
    queryFn: () =>
      apiJson<ProfessionalDirectoryProfileListResponse>(
        getToken,
        '/v1/admin/directory-profiles?status=pending',
      ),
    enabled: isLoaded && !!isSignedIn,
    retry: 1,
  });

  const coachOutreachRepliesQuery = useQuery({
    queryKey: COACH_OUTREACH_REPLIES_QUERY_KEY,
    queryFn: () =>
      apiJson<OutreachRepliesListResponse>(
        getToken,
        '/v1/admin/coach-outreach/replies?limit=50',
      ),
    enabled: isLoaded && !!isSignedIn,
    retry: 1,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const { hasUnread: hasUnreadCoachOutreachReplies } =
    useCoachOutreachUnreadReplies(coachOutreachRepliesQuery.data?.replies);

  const lawyerOutreachRepliesQuery = useQuery({
    queryKey: LAWYER_OUTREACH_REPLIES_QUERY_KEY,
    queryFn: () =>
      apiJson<OutreachRepliesListResponse>(
        getToken,
        '/v1/admin/lawyer-outreach/replies?limit=50',
      ),
    enabled: isLoaded && !!isSignedIn,
    retry: 1,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const { hasUnread: hasUnreadLawyerOutreachReplies } =
    useLawyerOutreachUnreadReplies(lawyerOutreachRepliesQuery.data?.replies);

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate({ to: '/auth/sign-in' });
    }
  }, [isLoaded, isSignedIn, navigate]);

  useEffect(() => {
    if (!userQuery.isFetched || !userQuery.data) return;
    if (!(userQuery.data.roles ?? []).includes('staff')) {
      navigate({ to: '/', search: { hero: undefined } });
    }
  }, [userQuery.isFetched, userQuery.data, navigate]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenGroupId(null);
  }, [pathname]);

  useEffect(() => {
    if (!openGroupId) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (
        desktopNavRef.current &&
        !desktopNavRef.current.contains(event.target as Node)
      ) {
        setOpenGroupId(null);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenGroupId(null);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openGroupId]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const html = document.documentElement;
    const { overflow: htmlOverflow } = html.style;
    const { overflow: bodyOverflow } = document.body.style;
    html.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileMenuOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      html.style.overflow = htmlOverflow;
      document.body.style.overflow = bodyOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileMenuOpen]);

  if (!isLoaded || userQuery.isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  if (!isSignedIn || !userQuery.data) return null;
  if (!(userQuery.data.roles ?? []).includes('staff')) return null;

  const usersActive =
    pathname === '/admin' ||
    pathname === '/admin/' ||
    pathname.startsWith('/admin/users');
  const pulseActive = pathname.startsWith('/admin/pulse');
  const narrativesActive = pathname.startsWith('/admin/narratives');
  const metricsActive = pathname.startsWith('/admin/metrics');
  const ltvActive = pathname.startsWith('/admin/ltv');
  const subscribersActive = pathname.startsWith('/admin/subscribers');
  const allTimeActive = pathname.startsWith('/admin/all-time');
  const performanceActive = pathname.startsWith('/admin/performance');
  const coachOutreachActive = pathname.startsWith('/admin/coach-outreach');
  const lawyerOutreachActive = pathname.startsWith('/admin/lawyer-outreach');
  const countyCourtOutreachActive = pathname.startsWith(
    '/admin/county-court-outreach',
  );
  const professionalsActive = pathname.startsWith('/admin/professionals');
  const appVersionActive = pathname.startsWith('/admin/app-version');
  const featureFlagsActive = pathname.startsWith('/admin/feature-flags');
  const divorceFormsActive = pathname.startsWith('/admin/divorce-forms');
  const toolsActive = pathname.startsWith('/admin/tools');
  const journalActive = pathname.startsWith('/admin/journal');
  const chatsActive = pathname.startsWith('/admin/chats');
  const messageAttachmentsActive = pathname.startsWith(
    '/admin/message-attachments',
  );
  const vaultActive = pathname.startsWith('/admin/vault');
  const adsActive = pathname.startsWith('/admin/ads');
  const attributionActive = pathname.startsWith('/admin/attribution');
  const tiktokActive = pathname.startsWith('/admin/tiktok');
  const seoActive = pathname.startsWith('/admin/seo');
  const marketingDomainsActive = pathname.startsWith('/admin/marketing-domains');
  const acquisitionActive = pathname.startsWith('/admin/acquisition');
  const yearlyTrialActive = pathname.startsWith('/admin/yearly-trial');
  const tabBase =
    'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2';
  const tabInactive = `${tabBase} text-gray-500 hover:text-gray-700`;
  const tabActive = `${tabBase} border border-gray-200 bg-harbor-50 text-primary`;
  const hasPendingDirectoryProfiles =
    (pendingDirectoryProfilesQuery.data?.profiles ?? []).length > 0;
  const handleSignOut = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      await signOut();
      navigate({ to: '/auth/sign-in' });
    }
  };

  const navGroups: AdminNavGroup[] = [
    {
      id: 'users-ops',
      label: 'Users & Ops',
      items: [
        { to: '/admin', label: 'Users', icon: FiUsers, active: usersActive },
        {
          to: '/admin/tools',
          label: 'Tools',
          icon: FiTool,
          active: toolsActive,
        },
        {
          to: '/admin/app-version',
          label: 'App Version',
          icon: FiSmartphone,
          active: appVersionActive,
        },
        {
          to: '/admin/feature-flags',
          label: 'Feature Flags',
          icon: FiFlag,
          active: featureFlagsActive,
        },
        {
          to: '/admin/performance',
          label: 'Performance',
          icon: FiClock,
          active: performanceActive,
          search: { range: '24h', method: undefined, template: undefined },
        },
      ],
    },
    {
      id: 'product',
      label: 'Product',
      items: [
        {
          to: '/admin/pulse',
          label: 'Pulse',
          icon: FiActivity,
          active: pulseActive,
          search: { date: undefined },
        },
        {
          to: '/admin/narratives',
          label: 'Narratives',
          icon: FiAlignLeft,
          active: narrativesActive,
          search: { date: undefined },
        },
        {
          to: '/admin/metrics',
          label: 'Metrics',
          icon: FiTrendingUp,
          active: metricsActive,
          search: { range: '30d' },
        },
        {
          to: '/admin/journal',
          label: 'Journal',
          icon: FiBook,
          active: journalActive,
        },
        {
          to: '/admin/chats',
          label: 'Chats',
          icon: FiMessageCircle,
          active: chatsActive,
        },
        {
          to: '/admin/message-attachments',
          label: 'Attachments',
          icon: FiImage,
          active: messageAttachmentsActive,
        },
        {
          to: '/admin/vault',
          label: 'Vault',
          icon: FiFolder,
          active: vaultActive,
        },
        {
          to: '/admin/divorce-forms',
          label: 'Divorce Forms',
          icon: FiFileText,
          active: divorceFormsActive,
        },
      ],
    },
    {
      id: 'growth',
      label: 'Growth',
      items: [
        {
          to: '/admin/acquisition',
          label: 'Acquisition',
          icon: FiFilter,
          active: acquisitionActive,
        },
        {
          to: '/admin/yearly-trial',
          label: '7-Day Trial',
          icon: FiMail,
          active: yearlyTrialActive,
        },
        {
          to: '/admin/tiktok',
          label: 'TikTok',
          icon: FiVideo,
          active: tiktokActive,
          search: { days: '30' },
        },
        {
          to: '/admin/ads',
          label: 'Ads',
          icon: FiBarChart2,
          active: adsActive,
          search: { days: '30' },
        },
        {
          to: '/admin/attribution',
          label: 'Attribution',
          icon: FiTarget,
          active: attributionActive,
          search: { days: '30' },
        },
        {
          to: '/admin/seo',
          label: 'SEO',
          icon: FiSearch,
          active: seoActive,
          search: { days: '30', tab: 'overview' },
        },
        {
          to: '/admin/marketing-domains',
          label: 'Marketing Domains',
          icon: FiLink,
          active: marketingDomainsActive,
          search: { days: '30', host: undefined },
        },
      ],
    },
    {
      id: 'revenue',
      label: 'Revenue',
      items: [
        {
          to: '/admin/ltv',
          label: 'LTV',
          icon: FiDollarSign,
          active: ltvActive,
          search: { range: '90d', lifetime: 'unbounded' },
        },
        {
          to: '/admin/subscribers',
          label: 'Subscribers',
          icon: FiPieChart,
          active: subscribersActive,
        },
        {
          to: '/admin/all-time',
          label: 'All Time',
          icon: FiGlobe,
          active: allTimeActive,
        },
      ],
    },
    {
      id: 'professionals',
      label: 'Professionals',
      items: [
        {
          to: '/admin/professionals',
          label: 'Professionals',
          icon: FiClipboard,
          active: professionalsActive,
          showPendingBadge: hasPendingDirectoryProfiles,
        },
        {
          to: '/admin/coach-outreach',
          label: 'Coach Outreach',
          icon: FiSend,
          active: coachOutreachActive,
          showUnreadBadge: hasUnreadCoachOutreachReplies,
          unreadBadgeLabel: 'New coach replies since you last opened Replies',
        },
        {
          to: '/admin/lawyer-outreach',
          label: 'Lawyer Outreach',
          icon: FiSend,
          active: lawyerOutreachActive,
          showUnreadBadge: hasUnreadLawyerOutreachReplies,
          unreadBadgeLabel: 'New lawyer replies since you last opened Replies',
        },
        {
          to: '/admin/county-court-outreach',
          label: 'County Court Outreach',
          icon: FiSend,
          active: countyCourtOutreachActive,
        },
      ],
    },
  ];

  const renderNavLink = (
    item: AdminNavItem,
    className: string,
    onNavigate?: () => void,
  ) => {
    const Icon = item.icon;
    return (
      <Link
        key={item.to}
        to={item.to}
        search={item.search}
        className={className}
        onClick={onNavigate}
      >
        <Icon className='h-4 w-4 shrink-0' aria-hidden />
        {item.label}
        {item.showPendingBadge ? (
          <AdminUnreadDot label='Pending listings to review' />
        ) : null}
        {item.showUnreadBadge ? (
          <AdminUnreadDot
            label={item.unreadBadgeLabel ?? 'New items to review'}
          />
        ) : null}
      </Link>
    );
  };

  const groupHasBadge = (group: AdminNavGroup) =>
    group.items.some(
      (item) => item.showPendingBadge || item.showUnreadBadge,
    );

  return (
    <div className='min-h-screen bg-gray-50'>
      <header
        className={
          mobileMenuOpen
            ? 'fixed inset-x-0 top-0 z-40 flex h-dvh max-h-dvh flex-col overflow-hidden bg-white px-4 py-4 sm:px-6 lg:sticky lg:top-0 lg:block lg:h-auto lg:max-h-none lg:overflow-visible lg:border-b lg:border-gray-200'
            : 'sticky top-0 z-40 border-b border-gray-200 bg-white px-4 py-4 sm:px-6'
        }
      >
        <div className='flex shrink-0 items-center justify-between gap-3'>
          <a
            href={ADMIN_DASHBOARD_HREF}
            className='flex min-w-0 items-center gap-2 text-lg font-semibold text-gray-900 hover:text-gray-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-lg sm:gap-3 sm:text-xl'
          >
            <img
              src={faviconPng}
              alt=''
              width={36}
              height={36}
              className='h-9 w-9 shrink-0 rounded-lg border border-sky-200 bg-white object-cover'
            />
            <span className='truncate'>Harbor Admin</span>
          </a>
          <div className='flex shrink-0 items-center gap-1'>
            <button
              type='button'
              onClick={() => setMobileMenuOpen((open) => !open)}
              className='lg:hidden rounded-full p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
              aria-expanded={mobileMenuOpen}
              aria-controls='admin-mobile-nav'
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? (
                <FiX className='h-6 w-6 shrink-0' aria-hidden />
              ) : (
                <FiMenu className='h-6 w-6 shrink-0' aria-hidden />
              )}
            </button>
            <button
              type='button'
              onClick={handleSignOut}
              className='flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
            >
              <FiLogOut className='h-4 w-4 shrink-0' aria-hidden />
              <span className='hidden sm:inline'>Sign out</span>
            </button>
          </div>
        </div>
        {mobileMenuOpen ? (
          <nav
            id='admin-mobile-nav'
            className='lg:hidden mt-4 flex min-h-0 flex-1 touch-pan-y flex-col gap-4 overflow-y-auto overscroll-contain border-t border-gray-200 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] [-webkit-overflow-scrolling:touch]'
          >
            {navGroups.map((group) => (
              <div key={group.id} className='flex flex-col gap-1'>
                <p className='px-4 text-xs font-semibold uppercase tracking-wide text-gray-400'>
                  {group.label}
                </p>
                {group.items.map((item) =>
                  renderNavLink(
                    item,
                    item.active
                      ? 'flex w-full items-center gap-3 rounded-lg bg-harbor-50 px-4 py-3 text-sm font-medium text-primary'
                      : 'flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100',
                    () => setMobileMenuOpen(false),
                  ),
                )}
              </div>
            ))}
          </nav>
        ) : null}
        <nav
          ref={desktopNavRef}
          className='mt-5 hidden flex-wrap items-center gap-2 lg:flex'
        >
          {navGroups.map((group) => {
            const groupActive = group.items.some((item) => item.active);
            const isOpen = openGroupId === group.id;
            return (
              <div key={group.id} className='relative'>
                <button
                  type='button'
                  onClick={() =>
                    setOpenGroupId((current) =>
                      current === group.id ? null : group.id,
                    )
                  }
                  className={groupActive ? tabActive : tabInactive}
                  aria-expanded={isOpen}
                  aria-haspopup='menu'
                >
                  {group.label}
                  {groupHasBadge(group) ? (
                    <AdminUnreadDot label={`Updates in ${group.label}`} />
                  ) : null}
                  <FiChevronDown
                    className={`h-4 w-4 shrink-0 transition-transform ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                    aria-hidden
                  />
                </button>
                {isOpen ? (
                  <div
                    role='menu'
                    className='absolute left-0 top-full z-50 mt-2 min-w-[12rem] rounded-xl border border-gray-200 bg-white py-1 shadow-lg'
                  >
                    {group.items.map((item) =>
                      renderNavLink(
                        item,
                        item.active
                          ? 'flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium text-primary bg-harbor-50'
                          : 'flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50',
                        () => setOpenGroupId(null),
                      ),
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </nav>
      </header>
      <main className='p-6'>
        <Outlet />
      </main>
    </div>
  );
}

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
});
